/**
 * Read-only selectors over GameState.
 *
 * Everything here is a pure function of state — no mutation, no RNG. The engine, the
 * AI, the charter tests, the scoring pass and the renderer all share these so there is
 * exactly one definition of "how many colonies does this player have".
 */

import {
  ALL_SEATS, BASE_CP, CP_PER_POPULATION, MAX_FLEETS, RESOURCE_KINDS, SYSTEM_CLASSES,
} from './constants';
import { distance, neighborKeys, parseKey } from './hex';
import { cpBonus, habitabilityBonus, hasTech, trackLevel } from './tech';
import type {
  Development, Fleet, GameState, HexKey, Player, PlayerId, Region, Resources,
  StarSystem, SystemClass,
} from './types';

/* ------------------------------------------------------------------- lookups */

export function playerOf(state: GameState, id: PlayerId): Player {
  const p = state.players[id];
  if (!p) throw new Error(`No player in seat ${id}`);
  return p;
}

export function systemOf(state: GameState, id: HexKey): StarSystem | undefined {
  return state.systems[id];
}

/** All systems, as an array. Callers must not mutate the result's members. */
export function allSystems(state: GameState): StarSystem[] {
  return Object.values(state.systems);
}

export function systemsOwnedBy(state: GameState, owner: PlayerId): StarSystem[] {
  return allSystems(state).filter((s) => s.owner === owner);
}

export function systemsWithDevelopment(
  state: GameState,
  owner: PlayerId,
  development: Development,
): StarSystem[] {
  return allSystems(state).filter((s) => s.owner === owner && s.development === development);
}

export function colonyCount(state: GameState, owner: PlayerId): number {
  return systemsWithDevelopment(state, owner, 'colony').length;
}

export function outpostCount(state: GameState, owner: PlayerId): number {
  return systemsWithDevelopment(state, owner, 'outpost').length;
}

/** Every system the player holds, of any development level. */
export function holdingCount(state: GameState, owner: PlayerId): number {
  return systemsOwnedBy(state, owner).length;
}

export function countOwnedOfClass(
  state: GameState,
  owner: PlayerId,
  cls: SystemClass,
): number {
  return allSystems(state).filter((s) => s.owner === owner && s.cls === cls).length;
}

export function countOwnedInRegion(
  state: GameState,
  owner: PlayerId,
  region: Region,
): number {
  return allSystems(state).filter((s) => s.owner === owner && s.region === region).length;
}

export function surveyedCount(state: GameState): number {
  return allSystems(state).filter((s) => s.surveyed).length;
}

/* -------------------------------------------------------------------- fleets */

export function fleetsOf(state: GameState, owner: PlayerId): Fleet[] {
  return Object.values(state.fleets).filter((f) => f.owner === owner);
}

export function fleetCount(state: GameState, owner: PlayerId): number {
  return fleetsOf(state, owner).length;
}

export function fleetsAt(state: GameState, systemId: HexKey): Fleet[] {
  return Object.values(state.fleets).filter((f) => f.at === systemId);
}

export function fleetsAtOwnedBy(
  state: GameState,
  systemId: HexKey,
  owner: PlayerId,
): Fleet[] {
  return Object.values(state.fleets).filter((f) => f.at === systemId && f.owner === owner);
}

export function hasFleetAt(state: GameState, systemId: HexKey, owner: PlayerId): boolean {
  for (const f of Object.values(state.fleets)) {
    if (f.at === systemId && f.owner === owner) return true;
  }
  return false;
}

export function canDeployMoreFleets(state: GameState, owner: PlayerId): boolean {
  return fleetCount(state, owner) < MAX_FLEETS;
}

/** Seats with at least one fleet in a system — the basis of contest detection. */
export function seatsPresentAt(state: GameState, systemId: HexKey): PlayerId[] {
  const seats = new Set<PlayerId>();
  for (const f of Object.values(state.fleets)) {
    if (f.at === systemId) seats.add(f.owner);
  }
  return ALL_SEATS.filter((s) => seats.has(s));
}

/** Systems where two or more seats have fleets. Sorted for determinism. */
export function contestedSystems(state: GameState): HexKey[] {
  const out: HexKey[] = [];
  for (const id of Object.keys(state.systems).sort()) {
    if (seatsPresentAt(state, id).length >= 2) out.push(id);
  }
  return out;
}

/* ------------------------------------------------------------- player derived */

/**
 * Is this system in an event blackout right now?
 *
 * One definition, used by production, the renderer and the inspect card — three places that
 * must never disagree about whether a hex is dark.
 */
export function isBlocked(system: StarSystem, round: number): boolean {
  return system.blockedFromRound <= round && round < system.blockedUntilRound;
}

/** Effective habitability, including the Living Worlds tech bonus. Capped at 3. */
export function effectiveHabitability(state: GameState, system: StarSystem): number {
  if (system.owner === null) return system.habitability;
  const owner = state.players[system.owner];
  if (!owner) return system.habitability;
  return Math.min(3, system.habitability + habitabilityBonus(owner));
}

/** Command Points the player should start a round with. */
export function commandPointsFor(state: GameState, id: PlayerId): number {
  const player = playerOf(state, id);
  return BASE_CP + Math.floor(player.population / CP_PER_POPULATION) + cpBonus(player);
}

/** How far a fleet may travel in one Move action. */
export function jumpRange(state: GameState, id: PlayerId): number {
  const player = playerOf(state, id);
  return 2 + trackLevel(player, 'drive') + (player.megastructure === 'gateNexus' ? 2 : 0);
}

/** Maximum population the player may hold. */
export function populationCap(player: Player): number {
  return 12 + (player.megastructure === 'ringworld' ? 1 : 0);
}

/**
 * Systems the player may legally survey right now: unsurveyed, and adjacent to
 * something the player has presence at (a fleet or an owned system).
 */
export function surveyTargets(state: GameState, id: PlayerId): HexKey[] {
  const presence = new Set<HexKey>();
  for (const f of fleetsOf(state, id)) presence.add(f.at);
  for (const s of systemsOwnedBy(state, id)) presence.add(s.id);

  const targets = new Set<HexKey>();
  for (const from of presence) {
    for (const nk of neighborKeys(parseKey(from))) {
      const sys = state.systems[nk];
      if (sys && !sys.surveyed) targets.add(nk);
    }
  }
  return [...targets].sort();
}

/** Systems the player could claim: surveyed, unclaimed, habitable, with their fleet. */
export function claimTargets(state: GameState, id: PlayerId): HexKey[] {
  const out: HexKey[] = [];
  for (const f of fleetsOf(state, id)) {
    const sys = state.systems[f.at];
    if (!sys || !sys.surveyed || sys.owner !== null) continue;
    if (sys.cls === 'deepSpace' || sys.habitability < 1) continue;
    if (!out.includes(sys.id)) out.push(sys.id);
  }
  return out.sort();
}

/** Outposts the player could grow into colonies. */
export function upgradeTargets(state: GameState, id: PlayerId): HexKey[] {
  return systemsWithDevelopment(state, id, 'outpost')
    .filter((s) => effectiveHabitability(state, s) >= 2)
    .map((s) => s.id)
    .sort();
}

/** Owned systems that could still be terraformed. */
export function terraformTargets(state: GameState, id: PlayerId): HexKey[] {
  return systemsOwnedBy(state, id)
    .filter((s) => s.habitability < 3 || s.hazard > 0)
    .map((s) => s.id)
    .sort();
}

/* ------------------------------------------------------------------ resources */

export function totalResources(res: Resources): number {
  return res.ore + res.fuel + res.biomass + res.data;
}

export function canAfford(have: Resources, cost: Resources): boolean {
  return (
    have.ore >= cost.ore &&
    have.fuel >= cost.fuel &&
    have.biomass >= cost.biomass &&
    have.data >= cost.data
  );
}

/** The first resource the player is short of, for a readable failure message. */
export function missingResource(have: Resources, cost: Resources): string | null {
  for (const kind of RESOURCE_KINDS) {
    if (have[kind] < cost[kind]) return kind;
  }
  return null;
}

export function addResources(a: Resources, b: Resources): Resources {
  return {
    ore: a.ore + b.ore,
    fuel: a.fuel + b.fuel,
    biomass: a.biomass + b.biomass,
    data: a.data + b.data,
  };
}

export function subtractResources(a: Resources, b: Resources): Resources {
  return {
    ore: a.ore - b.ore,
    fuel: a.fuel - b.fuel,
    biomass: a.biomass - b.biomass,
    data: a.data - b.data,
  };
}

export function scaleResources(a: Resources, factor: number): Resources {
  return {
    ore: a.ore * factor,
    fuel: a.fuel * factor,
    biomass: a.biomass * factor,
    data: a.data * factor,
  };
}

/**
 * Per-round production for one player, before the event modifier.
 *
 * Outposts yield the system's base; colonies yield double. Tech and megastructures
 * then add flat bonuses. Blocked systems (ion storm) contribute nothing.
 */
export function productionFor(state: GameState, id: PlayerId): Resources {
  const player = playerOf(state, id);
  let total: Resources = { ore: 0, fuel: 0, biomass: 0, data: 0 };

  for (const sys of systemsOwnedBy(state, id)) {
    if (isBlocked(sys, state.round)) continue;
    const multiplier = sys.development === 'colony' ? 2 : 1;
    total = addResources(total, scaleResources(sys.yield, multiplier));
  }

  const colonies = colonyCount(state, id);
  if (hasTech(player, 'industryI')) total.ore += colonies;
  if (hasTech(player, 'bioI')) total.biomass += 1;
  if (hasTech(player, 'scienceI')) total.data += 1;

  switch (player.megastructure) {
    case 'ringworld': total.biomass += 2; break;
    case 'dysonSwarm': total.fuel += 3; break;
    case 'arkFoundry': total.ore += 2; break;
    case 'gateNexus': break;
    case null: break;
  }
  return total;
}

/** Biomass the player owes for population upkeep this round. */
export function upkeepFor(state: GameState, id: PlayerId): number {
  const player = playerOf(state, id);
  const base = player.population;
  return hasTech(player, 'bioII') ? Math.floor(base / 2) : base;
}

/* -------------------------------------------------------------- region control */

/**
 * Region majority: seats ranked by holdings in that region. Ties share the rank, so
 * two seats on 3 systems each are both "first" and neither gets the runner-up award.
 */
export interface RegionStanding {
  region: Region;
  counts: Record<PlayerId, number>;
  leaders: PlayerId[];
  runnersUp: PlayerId[];
}

export function regionStanding(state: GameState, region: Region): RegionStanding {
  const counts = { 0: 0, 1: 0, 2: 0, 3: 0 } as Record<PlayerId, number>;
  for (const sys of allSystems(state)) {
    if (sys.region === region && sys.owner !== null) counts[sys.owner]++;
  }

  const scoring = ALL_SEATS.filter((s) => counts[s] > 0);
  const sorted = [...scoring].sort((a, b) => counts[b] - counts[a]);
  const top = sorted.length > 0 ? counts[sorted[0]!] : 0;
  const leaders = sorted.filter((s) => counts[s] === top);

  // Runner-up only exists if somebody is strictly below the leaders but above zero.
  const second = sorted.find((s) => counts[s] < top);
  const secondCount = second !== undefined ? counts[second] : 0;
  const runnersUp = secondCount > 0 ? sorted.filter((s) => counts[s] === secondCount) : [];

  return { region, counts, leaders, runnersUp };
}

/* ------------------------------------------------------------------- distance */

/** Hex distance between two systems by key. */
export function systemDistance(a: HexKey, b: HexKey): number {
  return distance(parseKey(a), parseKey(b));
}

/** A short label for a system, for the log and the inspect card. */
export function systemLabel(system: StarSystem): string {
  const def = SYSTEM_CLASSES[system.cls];
  return `${def.name} ${system.id}`;
}
